/**
 * F5-R6c · Admin 14 «Chat y notificaciones» — catálogo del dibujo del CEO
 * (2026-08-28), corregido contra el terreno MEDIDO el 2026-08-29.
 *
 * El principio del propio dibujo manda: «cada fila de la matriz lleva su tipo
 * real en un chip verde, o el chip gris "nuevo" si es de este rediseño — así
 * nadie confunde lo que existe con lo que hay que construir». El dibujo se
 * trazó sobre una foto vieja del código (5 tipos vivos); hoy hay DIEZ
 * emisores medidos, así que:
 *   · chat_message ES una notificación real (chat.module.ts, F-4 CHAT-FK),
 *     no sólo el contador /chat/unread-count que decía el dibujo;
 *   · «Screening devuelto con error» YA existe (your_screening_was_flagged,
 *     uploader.service.ts) — chip verde, no «nuevo»;
 *   · entran 3 filas vivas que el dibujo no conocía:
 *     screening_flagged_by_uploader (admin+supervisor),
 *     nav_form_reminder (navigator del caso + supervisores),
 *     findhelp_sla_breach_digest (lista opt-in de Configuración).
 * Todo lo demás (etiquetas, reglas, roles, defaults) es verbatim del dibujo.
 *
 * Este archivo es el ESPEJO 1:1 de las listas cerradas del server
 * (api/src/admin/system-config/config-kinds.ts — NOTIFY_ROLES/EVENTS/CHANNELS,
 * CHAT_RULE_KEYS). Se amplían en el mismo commit o el save rebota 400.
 */

export type NotifyRole = 'agent' | 'navigator' | 'uploader' | 'supervisor' | 'admin';
export type NotifyChannel = 'campana' | 'chat' | 'correo';

export const NOTIFY_ROLES: NotifyRole[] = ['agent', 'navigator', 'uploader', 'supervisor', 'admin'];
export const NOTIFY_CHANNELS: NotifyChannel[] = ['campana', 'chat', 'correo'];

export interface NotifyEventDef {
  /** id estable del evento (= el type vivo cuando existe emisor). */
  k: string;
  /** Chip verde con el type real, o null → chip gris «nuevo». */
  liveType: string | null;
  roles: NotifyRole[];
  /** null = canal bloqueado (guion con title, no casilla muerta). */
  ch: Record<NotifyChannel, boolean | null>;
  prio: 0 | 1 | 2;
  remind: 0 | 1 | 2 | 3;
}

/**
 * Los vivos arriba, los propuestos abajo (regla del dibujo).
 *
 * F5-R10 · `roles` REPARADO contra el código real (caminata del CEO
 * 2026-08-30). El dibujo había supuesto destinatarios que el server no
 * usa, así que la pantalla ofrecía apagar avisos a roles que nunca los
 * reciben — y NO ofrecía apagarlos a quienes sí. Medido emisor por emisor:
 *   · missed_booking      → el cron manda a admin+supervisor (decía agent/navigator)
 *   · reassign_requested  → solo admin (decía admin+supervisor)
 *   · attempts_reset      → el agente del último intento (decía agent+navigator)
 *   · notify_settings_changed → faltaba en la lista; va a los 5 roles menos el actor
 * `findhelp_sla_breach_digest` queda en admin como referencia, pero su
 * emisor NO mira el rol: usa la lista de ids opt-in
 * `findhelp_sla_digest_recipients` (ver la nota de la fila).
 */
export const NOTIFY_EVENTS: NotifyEventDef[] = [
  { k: 'safety_concern', liveType: 'safety_concern', roles: ['supervisor', 'admin'], ch: { campana: true, chat: true, correo: true }, prio: 2, remind: 3 },
  { k: 'missed_booking', liveType: 'missed_booking', roles: ['admin', 'supervisor'], ch: { campana: true, chat: false, correo: false }, prio: 1, remind: 2 },
  { k: 'reassign_requested', liveType: 'reassign_requested', roles: ['admin'], ch: { campana: true, chat: false, correo: false }, prio: 1, remind: 2 },
  { k: 'attempts_reset', liveType: 'attempts_reset', roles: ['agent'], ch: { campana: true, chat: false, correo: false }, prio: 0, remind: 0 },
  { k: 'bug_report_resolved', liveType: 'bug_report_resolved', roles: ['agent', 'navigator', 'uploader', 'supervisor', 'admin'], ch: { campana: true, chat: false, correo: false }, prio: 0, remind: 0 },
  { k: 'chat_message', liveType: 'chat_message', roles: ['agent', 'navigator', 'uploader', 'supervisor', 'admin'], ch: { campana: true, chat: true, correo: null }, prio: 2, remind: 0 },
  { k: 'your_screening_was_flagged', liveType: 'your_screening_was_flagged', roles: ['agent'], ch: { campana: true, chat: true, correo: false }, prio: 1, remind: 1 },
  // — las tres que el dibujo no conocía (terreno 2026-08-29) —
  { k: 'screening_flagged_by_uploader', liveType: 'screening_flagged_by_uploader', roles: ['admin', 'supervisor'], ch: { campana: true, chat: false, correo: false }, prio: 1, remind: 1 },
  { k: 'nav_form_reminder', liveType: 'nav_form_reminder', roles: ['navigator', 'supervisor'], ch: { campana: true, chat: false, correo: false }, prio: 1, remind: 2 },
  { k: 'findhelp_sla_breach_digest', liveType: 'findhelp_sla_breach_digest', roles: ['admin'], ch: { campana: true, chat: false, correo: false }, prio: 0, remind: 0 },
  { k: 'notify_settings_changed', liveType: 'notify_settings_changed', roles: ['agent', 'navigator', 'uploader', 'supervisor', 'admin'], ch: { campana: true, chat: false, correo: false }, prio: 0, remind: 0 },
  // — propuestos por el dibujo (chip «nuevo») —
  { k: 'claim_expiring', liveType: null, roles: ['uploader'], ch: { campana: true, chat: false, correo: null }, prio: 2, remind: 0 },
  { k: 'appointment_30m', liveType: null, roles: ['agent', 'navigator'], ch: { campana: true, chat: false, correo: false }, prio: 2, remind: 0 },
  { k: 'followup_overdue', liveType: null, roles: ['navigator'], ch: { campana: true, chat: false, correo: true }, prio: 1, remind: 2 },
  { k: 'preauth_pending', liveType: null, roles: ['supervisor'], ch: { campana: true, chat: true, correo: true }, prio: 2, remind: 2 },
  { k: 'preauth_denied', liveType: null, roles: ['navigator'], ch: { campana: true, chat: true, correo: false }, prio: 1, remind: 0 },
  { k: 'session_no_tracker', liveType: null, roles: ['navigator', 'admin'], ch: { campana: true, chat: false, correo: false }, prio: 1, remind: 3 },
  { k: 'batch_done', liveType: null, roles: ['admin'], ch: { campana: true, chat: false, correo: true }, prio: 0, remind: 0 },
  { k: 'bug_blocking', liveType: null, roles: ['admin', 'supervisor'], ch: { campana: true, chat: false, correo: false }, prio: 1, remind: 1 },
];

/** Override ralo de una celda de la matriz (lo que se guarda en notify_matrix). */
export interface MatrixCellOverride {
  campana?: boolean;
  chat?: boolean;
  correo?: boolean;
  prio?: 0 | 1 | 2;
  remind?: 0 | 1 | 2 | 3;
}
export type NotifyMatrix = Partial<Record<NotifyRole, Record<string, MatrixCellOverride>>>;

export interface EffectiveCell {
  campana: boolean | null;
  chat: boolean | null;
  correo: boolean | null;
  prio: 0 | 1 | 2;
  remind: 0 | 1 | 2 | 3;
}

/** Default del catálogo + override ralo → celda efectiva. Bloqueado (null) no se pisa. */
export function effectiveCell(def: NotifyEventDef, matrix: NotifyMatrix, role: NotifyRole): EffectiveCell {
  const ov = matrix[role]?.[def.k] ?? {};
  return {
    campana: def.ch.campana === null ? null : (ov.campana ?? def.ch.campana),
    chat: def.ch.chat === null ? null : (ov.chat ?? def.ch.chat),
    correo: def.ch.correo === null ? null : (ov.correo ?? def.ch.correo),
    prio: ov.prio ?? def.prio,
    remind: ov.remind ?? def.remind,
  };
}

/**
 * Devuelve la matriz RALA: sólo lo que difiere del default del catálogo.
 * Volver una celda a su default la BORRA del override (no queda basura).
 */
export function pruneMatrix(matrix: NotifyMatrix): NotifyMatrix {
  const out: NotifyMatrix = {};
  for (const role of NOTIFY_ROLES) {
    const evs = matrix[role];
    if (!evs) continue;
    for (const [k, ov] of Object.entries(evs)) {
      const def = NOTIFY_EVENTS.find((e) => e.k === k);
      if (!def) continue;
      const cell: MatrixCellOverride = {};
      for (const c of NOTIFY_CHANNELS) {
        if (def.ch[c] !== null && typeof ov[c] === 'boolean' && ov[c] !== def.ch[c]) cell[c] = ov[c];
      }
      if (typeof ov.prio === 'number' && ov.prio !== def.prio) cell.prio = ov.prio;
      if (typeof ov.remind === 'number' && ov.remind !== def.remind) cell.remind = ov.remind;
      if (Object.keys(cell).length > 0) {
        (out[role] ??= {})[k] = cell;
      }
    }
  }
  return out;
}

export const CHAT_RULE_KEYS = [
  'agentNavigator',
  'agentUploader',
  'uploaderNavigator',
  'anyoneSupervisor',
  'agentAgent',
] as const;
export type ChatRuleKey = (typeof CHAT_RULE_KEYS)[number];
export type ChatRules = Record<ChatRuleKey, boolean>;

/**
 * Semilla viva medida de getRecipientsList (la 080 siembra esto mismo):
 * el fallback si la clave todavía no existe en la base.
 */
export const CHAT_RULES_LIVE_DEFAULTS: ChatRules = {
  agentNavigator: false,
  agentUploader: true,
  uploaderNavigator: false,
  anyoneSupervisor: true,
  agentAgent: true,
};

export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type DayKey = (typeof DAY_KEYS)[number];
export type DayFlags = Record<DayKey, boolean>;

export const DAY_DEFAULTS: DayFlags = {
  mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false,
};

export interface ChatNotifyValues {
  notify_matrix: NotifyMatrix;
  notify_quiet_start: string;
  notify_quiet_end: string;
  notify_days: DayFlags;
  notify_dnd_in_call: boolean;
  notify_esc_claim_expired_min: number;
  notify_esc_followup_overdue_days: number;
  notify_esc_preauth_unsigned_hours: number;
  chat_rules: ChatRules;
  chat_retention_days: number;
}

/** Defaults del dibujo — sólo rigen si la clave no vino del server. */
export const GROUP_DEFAULTS: ChatNotifyValues = {
  notify_matrix: {},
  notify_quiet_start: '07:00',
  notify_quiet_end: '20:30',
  notify_days: DAY_DEFAULTS,
  notify_dnd_in_call: true,
  notify_esc_claim_expired_min: 2,
  notify_esc_followup_overdue_days: 3,
  notify_esc_preauth_unsigned_hours: 24,
  chat_rules: CHAT_RULES_LIVE_DEFAULTS,
  chat_retention_days: 730,
};

export const GROUP_KEYS = Object.keys(GROUP_DEFAULTS) as Array<keyof ChatNotifyValues>;

export const ESC_DEFS = [
  { key: 'notify_esc_claim_expired_min' as const, min: 1, max: 30, unitKey: 'f5r6c.unit.min' as const, labelKey: 'f5r6c.esc.claim.label' as const, ruleKey: 'f5r6c.esc.claim.rule' as const },
  { key: 'notify_esc_followup_overdue_days' as const, min: 1, max: 14, unitKey: 'f5r6c.unit.days' as const, labelKey: 'f5r6c.esc.followup.label' as const, ruleKey: 'f5r6c.esc.followup.rule' as const },
  { key: 'notify_esc_preauth_unsigned_hours' as const, min: 4, max: 72, unitKey: 'f5r6c.unit.h' as const, labelKey: 'f5r6c.esc.preauth.label' as const, ruleKey: 'f5r6c.esc.preauth.rule' as const },
];
